import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from 'src/store/middlewares/createAsyncThunkFromFetch';
import {
  addCardsToCollection as addCardsToCollectionApi,
  createCollection as createCollectionApi,
  createCollectionLocaleAPI,
  deleteCollection as deleteCollectionApi,
  deleteCollectionLocaleAPI,
  getCollection,
  getCollectionCards as getCollectionCardsApi,
  getCollectionLocalesAPI,
  getCollections,
  removeCardsFromCollection as removeCardsFromCollectionApi,
  updateCollection as updateCollectionApi,
  updateCollectionLocaleAPI,
} from './collections.api';
import { Collection } from './collections.types';

const name = 'collections';

const collectionEntityAdapter = createEntityAdapter<AsyncEntity<Collection>>({
  selectId: collection => collection.id,
});

export const collectionsSelectors = collectionEntityAdapter.getSelectors();

export const fetchCollections = createAsyncThunkFromFetch(`${name}/fetchCollections`, getCollections);
export const fetchCollection = createAsyncThunkFromFetch(`${name}/fetchCollection`, getCollection);
export const deleteCollection = createAsyncThunkFromFetch(`${name}/deleteCollection`, deleteCollectionApi);
export const createCollection = createAsyncThunkFromFetch(`${name}/createCollection`, createCollectionApi);
export const updateCollection = createAsyncThunkFromFetch(`${name}/updateCollection`, updateCollectionApi);

export const fetchCollectionCards = createAsyncThunkFromFetch(`${name}/fetchCollectionCards`, getCollectionCardsApi);
export const addCardsToCollection = createAsyncThunkFromFetch(`${name}/addCardsToCollection`, addCardsToCollectionApi);
export const removeCardsFromCollection = createAsyncThunkFromFetch(
  `${name}/removeCardsFromCollection`,
  removeCardsFromCollectionApi
);

export const fetchCollectionLocales = createAsyncThunkFromFetch(
  `${name}/fetchCollectionLocales`,
  getCollectionLocalesAPI
);
export const createCollectionLocale = createAsyncThunkFromFetch(
  `${name}/createCollectionLocale`,
  createCollectionLocaleAPI
);
export const updateCollectionLocale = createAsyncThunkFromFetch(
  `${name}/updateCollectionLocale`,
  updateCollectionLocaleAPI
);
export const deleteCollectionLocale = createAsyncThunkFromFetch(
  `${name}/deleteCollectionLocale`,
  deleteCollectionLocaleAPI
);

const collectionsSlice = createSlice({
  name,
  initialState: collectionEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    // Collections
    builder.addCase(fetchCollections.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchCollections.fulfilled, (state, { payload }) => {
      collectionEntityAdapter.setAll(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchCollection.fulfilled, (state, { payload }) => {
      collectionEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(fetchCollections.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteCollection.pending, (state, { meta }) => {
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteCollection.fulfilled, (state, { meta }) => {
      collectionEntityAdapter.removeOne(state, meta.arg.collectionId);
    });
    builder.addCase(createCollection.fulfilled, (state, { payload }) => {
      collectionEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateCollection.fulfilled, (state, { meta, payload }) => {
      collectionEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(addCardsToCollection.pending, (state, { meta }) => {
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { isPending: true },
      });
    });
    builder.addCase(addCardsToCollection.fulfilled, (state, { meta, payload }) => {
      const collection = collectionsSelectors.selectById(state, meta.arg.collectionId) as Collection;
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: {
          cards: [...collection.cards, ...payload],
          cardsOrder: [...collection.cardsOrder, ...payload.map(p => p.id)],
          isPending: false,
        },
      });
    });
    builder.addCase(removeCardsFromCollection.pending, (state, { meta }) => {
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { isPending: true },
      });
    });
    builder.addCase(removeCardsFromCollection.fulfilled, (state, { meta }) => {
      const collection = collectionsSelectors.selectById(state, meta.arg.collectionId) as Collection;
      const cards = collection.cards.filter(c => !meta.arg.data.ids.includes(c.id));
      const cardsOrder = collection.cardsOrder.filter(c => !meta.arg.data.ids.includes(c));
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: {
          cards,
          cardsOrder,
          isPending: false,
        },
      });
    });
    builder.addCase(fetchCollectionCards.pending, (state, { meta }) => {
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { isPending: true },
      });
    });
    builder.addCase(fetchCollectionCards.fulfilled, (state, { meta, payload }) => {
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: {
          cards: [...payload],
          isPending: false,
        },
      });
    });
    builder.addCase(fetchCollectionLocales.fulfilled, (state, { meta, payload }) => {
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { locales: payload, isPending: false },
      });
    });
    builder.addCase(createCollectionLocale.fulfilled, (state, { meta, payload }) => {
      const content = collectionsSelectors.selectById(state, meta.arg.collectionId) as Collection;
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { locales: [payload, ...content.locales], isPending: false },
      });
    });
    builder.addCase(updateCollectionLocale.fulfilled, (state, { meta, payload }) => {
      const content = collectionsSelectors.selectById(state, meta.arg.collectionId) as Collection;
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { locales: content.locales.map(l => (l.id === meta.arg.localeId ? payload : l)), isPending: false },
      });
    });
    builder.addCase(deleteCollectionLocale.fulfilled, (state, { meta }) => {
      const content = collectionsSelectors.selectById(state, meta.arg.collectionId) as Collection;
      collectionEntityAdapter.updateOne(state, {
        id: meta.arg.collectionId,
        changes: { locales: content.locales.filter(l => l.id !== meta.arg.localeId), isPending: false },
      });
    });
  },
});

const { reducer } = collectionsSlice;

export default reducer;
